/**
 * Utility for caching search and chat input with filters in localStorage
 *
 * Cache structure:
 * - Homepage: 'search-chat-cache-{userId}-home'
 * - On specific chat: 'search-chat-cache-{userId}-{chatId}'
 *
 * Each cache entry stores:
 * - query: the input text
 * - filters: projectIds, branchNames, timeFilter, agentNames
 * - mode: "search" | "chat"
 * - timestamp: the timestamp of the cache entry
 *
 * Note: userId is included to ensure proper user isolation
 */

export interface CacheFilters {
  projectIds: string[];
  branchNames: string[];
  timeFilter: "today" | "week" | "month" | null;
  agentNames: string[];
}

export interface CacheEntry {
  query: string;
  filters: CacheFilters;
  mode: "search" | "chat";
  timestamp: number;
}

/**
 * Cache namespace for managing search and chat input cache
 */
export const SearchChatCache = {
  /**
   * Generate a cache key based on userId, and route
   * @param userId - Current user ID (for isolation)
   * @param pathname - Current route pathname
   * @returns Cache key or null if caching should be skipped
   */
  getCacheKey(
    userId: string | null | undefined,
    pathname: string
  ): string | null {
    // Don't cache if no user ID
    if (!userId) return null;

    return `search-chat-cache-${userId}-${pathname}`;
  },

  /**
   * Save cache entry to localStorage
   * @param key - Cache key
   * @param entry - Cache entry to save
   */
  save(key: string, entry: CacheEntry): void {
    try {
      localStorage.setItem(key, JSON.stringify(entry));
    } catch (error) {
      console.warn("Failed to save search/chat cache:", error);
    }
  },

  /**
   * Load cache entry from localStorage
   * @param key - Cache key
   * @returns Cache entry or null if not found
   */
  load(key: string): CacheEntry | null {
    try {
      const data = localStorage.getItem(key);
      if (!data) return null;

      const entry = JSON.parse(data) as CacheEntry;

      // Validate structure
      if (!entry || typeof entry.query !== "string" || !entry.filters) {
        return null;
      }

      return entry;
    } catch (error) {
      console.warn("Failed to load search/chat cache:", error);
      return null;
    }
  },

  /**
   * Delete cache entry from localStorage
   * @param key - Cache key
   */
  delete(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.warn("Failed to delete search/chat cache:", error);
    }
  },

  /**
   * Clear all cache entries
   */
  clearAll(): void {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach((key) => {
        if (key.startsWith("search-chat-cache-")) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn("Failed to clear search/chat cache:", error);
    }
  },

  /**
   * Clean up old cache entries (older than 30 days)
   * This method checks if cleanup was run today and skips if already done
   */
  cleanup(): void {
    try {
      const CLEANUP_KEY = "search-chat-cache-last-cleanup";
      const lastCleanup = localStorage.getItem(CLEANUP_KEY);
      const today = new Date().toDateString();

      // Skip if already cleaned up today
      if (lastCleanup === today) {
        return;
      }

      const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
      const keys = Object.keys(localStorage);

      keys.forEach((key) => {
        if (key.startsWith("search-chat-cache-")) {
          const entry = this.load(key);
          if (entry && entry.timestamp < thirtyDaysAgo) {
            this.delete(key);
          }
        }
      });

      // Mark cleanup as done for today
      localStorage.setItem(CLEANUP_KEY, today);
    } catch (error) {
      console.warn("Failed to cleanup search/chat cache:", error);
    }
  },

  /**
   * Get cache entry based on mode, userId, and pathname
   * @param userId - Current user ID
   * @param pathname - Current route pathname
   * @returns Cache entry or null
   */
  get(userId: string | null | undefined, pathname: string): CacheEntry | null {
    const key = this.getCacheKey(userId, pathname);
    if (!key) return null;
    return this.load(key);
  },

  /**
   * Set cache entry based on mode, userId, and pathname
   * @param userId - Current user ID
   * @param pathname - Current route pathname
   * @param mode - Current mode
   * @param query - Input query
   * @param filters - Filter context
   */
  set(
    userId: string | null | undefined,
    pathname: string,
    mode: "search" | "chat",
    query: string,
    filters: CacheFilters
  ): void {
    const key = this.getCacheKey(userId, pathname);
    if (!key) return;

    const entry: CacheEntry = {
      mode,
      query,
      filters,
      timestamp: Date.now(),
    };

    this.save(key, entry);
  },

  /**
   * Clear cache for specific mode, userId, and pathname
   * @param userId - Current user ID
   * @param pathname - Current route pathname
   */
  clear(userId: string | null | undefined, pathname: string): void {
    const key = this.getCacheKey(userId, pathname);
    if (!key) return;
    this.delete(key);
  },
};
